import { useLayoutEffect, useRef } from "react";
import { useMatch } from "react-router-dom";
import { Button } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import { useRestaurantNotifications } from "./NotificationsProvider";

/**
 * The height the notice occupies, published on `<html>` for every full-height
 * shell to subtract (`.app-shell` in styles.css, the storefront editor). The
 * notice stays in flow above the screen, so it never covers the top bar's
 * controls, and the shell shrinks by exactly its height, so it never pushes the
 * bottom strip (the till's settle bar, the board's footer) out of the viewport.
 */
export const REALTIME_NOTICE_HEIGHT_VAR = "--realtime-notice-h";

/** Keep the working screen and unsaved input visible during a live-update outage. */
export function RealtimeNotice() {
  const t = useTranslate();
  const { realtimeStatus, bootstrapStatus, retryBootstrap } = useRestaurantNotifications();
  const ref = useRef<HTMLDivElement>(null);
  /* The wall board draws its OWN full-width band (`KitchenOfflineBand`, T12):
     on `/:locale/kitchen` this notice yields, so a cook sees one warning, not
     two, and its height variable stays unset. `/kitchen/availability` keeps it. */
  const onKitchenBoard = useMatch("/:locale/kitchen") !== null;
  const visible = !onKitchenBoard && (realtimeStatus === "degraded" || realtimeStatus === "unconfigured" || bootstrapStatus === "unavailable");
  useLayoutEffect(() => {
    const node = ref.current;
    if (!visible || !node) return;
    const root = document.documentElement;
    const publish = () => root.style.setProperty(REALTIME_NOTICE_HEIGHT_VAR, `${Math.ceil(node.getBoundingClientRect().height)}px`);
    publish();
    // The copy wraps on a phone and changes with the locale.
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(publish);
    observer?.observe(node);
    return () => { observer?.disconnect(); root.style.removeProperty(REALTIME_NOTICE_HEIGHT_VAR); };
  }, [visible]);
  if (!visible) return null;
  return <div ref={ref} role="alert" data-realtime-notice="" className="flex flex-wrap items-center justify-between gap-3 border-b border-[color:var(--warning)] bg-[var(--bg-surface)] px-4 py-3 text-sm">
    <p>{realtimeStatus === "unconfigured"
      ? t((m) => m.systemScreens.realtimeUnconfigured)
      : t((m) => m.systemScreens.realtimeUnavailable)}</p>
    <Button variant="secondary" onClick={retryBootstrap}>{t((m) => m.common.retry)}</Button>
  </div>;
}
